<template>
  <v-col class="register default-slot" md="7" sm="12">
    <v-dialog v-model="dialog" width="500px">
      <v-card class="confirmation-modal">
        <v-img
          class="modal-icon"
          width="84"
          :src="thumbsUpImage"
        />
        <h2 class="mt-8 mb-8">
          Thank you, your lease agreement has been submitted successfully.
        </h2>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogError" width="500px">
      <v-card class="confirmation-modal">
        <h2 class="mt-8 mb-8">
          {{ errorMessage }}
        </h2>
      </v-card>
    </v-dialog>
    <div class="top-icons">
      <a
        class="back-icon"
        v-on:click="
          updateSignature(sig);
          changePage(2);
        "
      >
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
      </a>
    </div>
    <h1 class="register-title">Lease Agreement</h1>
    <v-form class="register-form" v-model="isValid">
      <div class="table-gray-box">
        <table>
          <tr>
            <td>Lease Agreement</td>
            <td>
              ${{ validationInfo.fee
              }}<v-tooltip bottom content-class="fee-table-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"
                    ><font-awesome-icon icon="info-circle"
                  /></span>
                </template>
                <simple-fee-table :priceInfo="priceInfo" />
              </v-tooltip>
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td>${{ validationInfo.fee }}</td>
          </tr>
        </table>
      </div>
      <div class="table-gray-box">
        <table>
          <tr>
            <td><b>Leased Aussie Info</b></td>
          </tr>
          <tr>
            <td>Name</td>
            <td>{{ dogInfo.name }}</td>
          </tr>
          <tr>
            <td>ASCA ID</td>
            <td>{{ dogInfo.dogId }}</td>
          </tr>
          <tr>
            <td>Main Color</td>
            <td>{{ dogInfo.body }}</td>
          </tr>
          <tr>
            <td>DNA Status</td>
            <td>{{ dogInfo.dnaStatus }}</td>
          </tr>
          <tr>
            <td><b>Owners</b></td>
          </tr>
          <tr>
            <CoownersList
              :owners="dogInfo.owners"
              :ownershipType="dogOwnershipType"
              :hideHeader="true"
            />
          </tr>
        </table>
      </div>
      <div class="table-gray-box">
        <table>
          <tr>
            <td><b>Lessee(s)</b></td>
          </tr>
          <tr>
            <CoownersList
              :owners="lesseeInfo"
              :ownershipType="leaseOwnershipType"
              :hideHeader="true"
            />
          </tr>
        </table>  
      </div>
      <signature class="mt-3" :signature="sig" v-on:update="sig = $event">
        <template v-slot:signatureText>
          <lease-signature />
        </template>
      </signature>
    </v-form>
    <v-overlay :value="processingRequest">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <hosted-registration-payment
      :disabled="!isValid || sig === '' || processingRequest"
      buttonText="SUBMIT"
      v-on:updatePaymentInfo="($event) => {
        processingRequest = true;
        submitRequest($event, sig);
      }"
    />
    <p class="steps">STEP <b>3</b> OF <b>3</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import router from "@/router";

import LeaseSignature from "@/components/common/LeaseSignature.vue";
import Signature from "@/components/common/inputs/Signature.vue";
import SimpleFeeTable from "@/components/common/SimpleFeeTable.vue";
import FooterText from "@/components/common/FooterText.vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import HostedRegistrationPayment from "@/components/common/HostedRegistrationPayment.vue";

const thumbsUpImage = require('@/assets/thumbs-up-duotone.svg');

export default Vue.extend({
  components: {
    SimpleFeeTable,
    LeaseSignature,
    Signature,
    FooterText,
    CoownersList,
    HostedRegistrationPayment,
  },
  name: "LeaseRequestForm3",
  data: function () {
    return {
      thumbsUpImage,
      dialog: false,
      dialogError: false,
      isValid: true,
      sig: this.$props.signature,
      processingRequest: false,
      errorMessage: "",
    };
  },
  methods: {
    openModal: function () {
      this.$data.dialog = true;
    },
    openErrorModal: function () {
      this.$data.dialogError = true;
    },
  },
  props: {
    changePage: Function,
    submitRequest: Function,
    validationInfo: Object,
    updatePaymentInfo: Function,
    dogInfo: Object,
    lesseeInfo: Array,
    priceInfo: Object,
    updateSignature: Function,
    signature: String,
    status: Object,
  },
  watch: {
    status: function (newVal: any, oldVal: any) {
      if (newVal?.success) {
        this.openModal();
      } else if (newVal?.data?.error) {            // Actual descriptive message received from the API
        this.errorMessage = newVal.data.error;
        this.openErrorModal();
      } else if (newVal?.message) {                        // Likely "Request failed with status code 500" or something, but definitely an error
        this.errorMessage = newVal.message;
        this.openErrorModal();
      } else {                                            // Something really weird happened in this case
        this.errorMessage = "An unknown error occurred.";
        this.openErrorModal();
      }
    },
    dialog: function (newVal: boolean, oldVal: boolean) {
      if (!newVal) {
        this.$store.dispatch("loadPersonTasks", this.userInfo.personId);
        router.push({ path: "/" });
      }
    },
    dialogError: function (newVal: boolean, oldVal: boolean) {
      if (!newVal && this.status?.data?.paymentIssue) {
        this.processingRequest = false;
        this.$store.dispatch("loadPersonTasks", this.userInfo.personId);
        router.push({ path: "/" });
      } else if (!newVal) {
        this.processingRequest = false;
      }
    },
  },
  computed: {
    leaseOwnershipType: function() {
      return OwnershipType.Lease;
    },
    dogOwnershipType: function() {
      return OwnershipType.Dog;
    },
    userInfo: function() {
      return this.$store.state.userInfo;
    },
  }
});
</script>
